import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../../common/reducers/utilities";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = d => d.id;

export const equipmentDocumentSlice = createSlice({
    name: 'equipmentDocs',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            state.data[action.payload.equipmentid] = action.payload.data; 
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byEquipment = groupBy(toUpdate, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                state.data[equipmentid] = (state.data[equipmentid]?.filter(d => !ids.has(key(d))) ?? []).concat(docs);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byEquipment = groupBy(toRemove, 'equipmentid');
            Object.entries(byEquipment).forEach(([ equipmentid, docs ]) => {
                if (state.data[equipmentid]) {
                    const ids = new Set(docs.map(d => key(d)));
                    state.data[equipmentid] = state.data[equipmentid].filter(d => !ids.has(key(d)));
                    if (state.data[equipmentid].length === 0) {
                        delete state.data[equipmentid];
                    }
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentDocumentSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipmentDocs', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipmentDocs');

const fetchEquipmentDocs = equipmentid => network => network.get('/api/v1/equipmentDocs/' + encodeURIComponent(equipmentid), data => equipmentDocumentSlice.actions.set({ equipmentid, data}), visualizeFailure('get'));
export const fetchEquipmentDoc = async (network, id) => network.fetchData('/api/v1/equipmentDoc/' + encodeURIComponent(id));
export const addEquipmentDoc = (network, doc) => {
    let data = new FormData();
    data.append('equipmentid', doc.equipmentid)
    data.append('file', doc.file);
    data.append('filename', doc.file.name);
    return network.postData('/api/v1/equipmentDoc', data)
};
export const updateEquipmentDoc = (network, data) => network.put('/api/v1/equipmentDoc', data, equipmentDocumentSlice.addOrUpdate, visualizeFailure('update'));
export const removeEquipmentDoc = (network, id) => network.delete('/api/v1/equipmentDoc/' + encodeURIComponent(id), equipmentDocumentSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectAllEquipmentDocData = state => state.equipmentDocs.data;
const selectEquipmentDocs = equipmentid => createSelector([selectAllEquipmentDocData], docs => docs[equipmentid] ?? []);
// #endregion

const listen = {
    equipmentDocCreated: (doc, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment_doc', key(doc));
        dispatch(equipmentDocumentSlice.actions.addOrUpdate(doc))
    },
    equipmentDocRemoved: (doc, { dispatch }) => dispatch(equipmentDocumentSlice.actions.remove(doc))
}
const equipmentInitDocsByID = initMapFactory(fetchEquipmentDocs, listen)
registerEntityBasedFetch('equipment', equipmentInitDocsByID);

const cleanup = { root: equipmentDocumentSlice.name, all: state => Object.values(selectAllEquipmentDocData(state)).flat(), removeAll: doc => equipmentDocumentSlice.actions.remove(doc), key };
export const [useEquipmentDocs] = dataHookFactory(selectEquipmentDocs, equipmentInitDocsByID, {cleanup});

const reducer = { name: equipmentDocumentSlice.name, reducer: equipmentDocumentSlice.reducer }
export default reducer;